/**
 * Same nameless-stub predicate as jamapp-firebase/scripts/unnamed-venue-predicate.js.
 * Status `stub` is included here so the portal can hide docs the create trigger
 * refused to mark pending. The cleanup script does not delete status `stub`.
 */

type VenueLike = {
  name?: unknown;
  businessName?: unknown;
  tradingName?: unknown;
  displayName?: unknown;
  address?: unknown;
  location?: { formattedAddress?: unknown; address?: unknown } | null;
  ownerId?: unknown;
  createdBy?: unknown;
  ownership?: {
    adminId?: unknown;
    adminEmail?: unknown;
    adminName?: unknown;
  } | null;
  status?: unknown;
  portalStatus?: unknown;
};

function isBlankName(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value !== 'string') return true;
  return value.trim() === '';
}

function isBlankOwnerField(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

function isBlankAddressValue(address: unknown): boolean {
  if (address == null) return true;
  if (typeof address === 'string') return address.trim() === '';
  if (typeof address === 'object' && !Array.isArray(address)) {
    return !Object.values(address as Record<string, unknown>).some((value) => {
      if (typeof value === 'string') return value.trim() !== '';
      return value != null && typeof value !== 'string';
    });
  }
  return false;
}

function hasNoAddress(data: VenueLike): boolean {
  if (!isBlankAddressValue(data.address)) return false;
  const location = data.location;
  if (!location || typeof location !== 'object') return true;
  if (!isBlankAddressValue(location.formattedAddress)) return false;
  if (!isBlankAddressValue(location.address)) return false;
  return true;
}

function hasNoOwner(data: VenueLike): boolean {
  const ownership = data.ownership && typeof data.ownership === 'object' ? data.ownership : {};
  return isBlankOwnerField(data.ownerId) &&
    isBlankOwnerField(ownership.adminId) &&
    isBlankOwnerField(ownership.adminEmail) &&
    isBlankOwnerField(ownership.adminName) &&
    isBlankOwnerField(data.createdBy);
}

function hasDeletableStatus(data: VenueLike): boolean {
  const status = data.status;
  const portalStatus = data.portalStatus;
  if (typeof status === 'string' && status.trim() === 'pending') return true;
  if (status != null && typeof status !== 'string') return false;
  const statusBlank = status == null || (typeof status === 'string' && status.trim() === '');
  if (!statusBlank) return false;
  if (portalStatus == null) return true;
  if (typeof portalStatus !== 'string') return false;
  const trimmed = portalStatus.trim();
  return trimmed === '' || trimmed === 'pending';
}

export function isNamelessVenue(data: VenueLike | null | undefined): boolean {
  if (!data || typeof data !== 'object') return false;
  if (![data.name, data.businessName, data.tradingName, data.displayName].every(isBlankName)) {
    return false;
  }
  if (!hasNoAddress(data)) return false;
  if (data.status === 'stub') return true;
  return hasNoOwner(data) && hasDeletableStatus(data);
}

export function venueUsageFromDoc(data: any) {
  return {
    offersUsed: data?.usage?.offersUsed ?? data?.subscription?.offersUsed ?? 0,
    offersLimit: data?.usage?.offersLimit ?? data?.subscription?.offersLimit ?? 5,
    lastResetDate: data?.usage?.lastResetDate ?? data?.subscription?.lastResetDate ?? null,
  };
}
